
import { useState, useEffect, useRef, useCallback } from 'react';

const SECTION_MESSAGES: Record<string, string> = {
  inicio: '¡Hola! Bienvenido al CDE MIPYME Valle de Sula 👋',
  'quienes-somos': 'Conoce nuestra misión y visión institucional.',
  'video-institucional': '¡Mira nuestro video y descubre todo lo que hacemos!',
  servicios: 'Tenemos servicios diseñados especialmente para tu empresa.',
  empleabilidad: 'Conectamos talento con oportunidades de empleo.',
  programas: 'Explora nuestros programas de desarrollo empresarial.',
  beneficios: '¡Muchos beneficios te esperan al unirte a nosotros!',
  noticias: 'Mantente informado con nuestras últimas noticias.',
  galeria: 'Mira todo lo que hemos logrado juntos.',
  testimonios: 'Empresarios como tú ya confían en nosotros.',
  contacto: '¿Tienes dudas? ¡Escríbenos, con gusto te ayudamos!',
};

const SECTION_IDS = Object.keys(SECTION_MESSAGES);

export default function MascotaFlotante() {
  const [visible, setVisible] = useState(true);
  const [bubble, setBubble] = useState<string | null>(null);
  const [showBubble, setShowBubble] = useState(false);

  const sectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSectionRef = useRef<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  const showMessage = useCallback((sectionId: string) => {
    const msg = SECTION_MESSAGES[sectionId];
    if (!msg) return;

    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);

    setShowBubble(false);
    setBubble(msg);

    setTimeout(() => {
      setShowBubble(true);
      bubbleTimerRef.current = setTimeout(() => {
        setShowBubble(false);
      }, 6000);
    }, 80);
  }, []);

  useEffect(() => {
    const sectionVisibility: Record<string, number> = {};

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sectionVisibility[entry.target.id] = entry.intersectionRatio;
        });

        let maxRatio = 0;
        let mostVisible = '';
        Object.entries(sectionVisibility).forEach(([id, ratio]) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            mostVisible = id;
          }
        });

        if (mostVisible && mostVisible !== lastSectionRef.current) {
          lastSectionRef.current = mostVisible;

          if (sectionTimerRef.current) clearTimeout(sectionTimerRef.current);
          sectionTimerRef.current = setTimeout(() => {
            showMessage(mostVisible);
          }, 5000);
        }
      },
      { threshold: [0.1, 0.3, 0.5, 0.7] }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    // Mensaje de bienvenida al cargar
    const initTimer = setTimeout(() => {
      showMessage('inicio');
      lastSectionRef.current = 'inicio';
    }, 3000);

    return () => {
      clearTimeout(initTimer);
      if (sectionTimerRef.current) clearTimeout(sectionTimerRef.current);
      if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
      observerRef.current?.disconnect();
    };
  }, [showMessage]);

  if (!visible) return null;

  return (
    <div
      className="fixed z-[9999] flex flex-col items-end gap-2 select-none"
      style={{ bottom: '24px', right: '24px' }}
      aria-label="Mascota CDE"
    >
      {/* Burbuja de texto */}
      <div
        style={{
          transition: 'opacity 0.45s ease, transform 0.45s ease',
          opacity: showBubble ? 1 : 0,
          transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.94)',
          pointerEvents: showBubble ? 'auto' : 'none',
        }}
        className="relative"
      >
        <div
          style={{
            background: '#ffffff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
            borderRadius: '16px 16px 4px 16px',
            padding: '10px 16px',
            maxWidth: '220px',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '13px',
            fontWeight: 500,
            color: '#1A3A52',
            lineHeight: '1.45',
            textAlign: 'right',
            position: 'relative',
          }}
        >
          {bubble}
          {/* Cola de burbuja */}
          <span
            style={{
              position: 'absolute',
              bottom: '-8px',
              right: '20px',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '0px solid transparent',
              borderTop: '8px solid #ffffff',
            }}
          />
        </div>
      </div>

      {/* Mascota + botón cerrar */}
      <div className="relative">
        {/* Botón X */}
        <button
          onClick={() => {
            setVisible(false);
            if (sectionTimerRef.current) clearTimeout(sectionTimerRef.current);
            if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
          }}
          className="absolute -top-2 -left-2 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors cursor-pointer"
          style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.18)' }}
          aria-label="Cerrar mascota"
          title="Cerrar"
        >
          <i className="ri-close-line" style={{ fontSize: '11px' }} />
        </button>

        {/* Imagen con animación flotante */}
        <div style={{ animation: 'mascotaFloat 3.6s ease-in-out infinite' }}>
          <img
            src="https://public.readdy.ai/ai/img_res/7a5e1106-911b-46f3-8705-762335065abe.png"
            alt="Mascota CDE MIPYME"
            style={{
              height: '90px',
              width: '90px',
              objectFit: 'cover',
              display: 'block',
              borderRadius: '50%',
              boxShadow: '0 6px 28px rgba(0,0,0,0.15)',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes mascotaFloat {
          0%   { transform: translateY(0px); }
          50%  { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        @media (max-width: 768px) {
          .mascota-fixed-wrap img {
            height: 70px !important;
            width: 70px !important;
          }
          .mascota-fixed-wrap {
            bottom: 16px !important;
            right: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
